/* Consent-gated analytics.
 *
 * Nothing is loaded or sent until the visitor grants consent via the cookie
 * banner. PostHog (product analytics) and, if configured, Google Analytics 4
 * are initialised only after consent. Owner-provided IDs are read from env
 * placeholders so no fake IDs are hard-coded.
 */

const CONSENT_KEY = "fedari_cookie_consent";

// True during the react-snap prerender pass — never load analytics scripts or
// send events while prerendering (avoids ERR_FAILED noise in snapshot logs).
const IS_SNAPSHOT = typeof navigator !== "undefined" && /ReactSnap/.test(navigator.userAgent);

// Public PostHog project key (client-side, not a secret).
const POSTHOG_KEY = "phc_DbsPb39SRc8z3EiQ6Dhj6ikv4H4rTKcht9d4sZSesceP";
const POSTHOG_HOST = "https://ap.emergent.sh";

// Owner-provided IDs — leave blank until supplied.
const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || "";

let loaded = false;

export const getConsent = () =>
  typeof window !== "undefined" ? window.localStorage.getItem(CONSENT_KEY) : null;

export const setConsent = (value) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CONSENT_KEY, value);
  if (value === "granted") loadAnalytics();
};

function loadPostHog() {
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV || ((window.posthog = e), (e._i = []), (e.init = function (i, s, a) {
      function g(t, e) { var o = e.split("."); 2 == o.length && ((t = t[o[0]]), (e = o[1])), (t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }); }
      (p = t.createElement("script")).type = "text/javascript", p.crossOrigin = "anonymous", p.async = !0, p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js", (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
      var u = e; for (void 0 !== a ? (u = e[a] = []) : (a = "posthog"), u.people = u.people || [], u.toString = function (t) { var e = "posthog"; return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e; }, u.people.toString = function () { return u.toString(1) + ".people (stub)"; }, o = "init capture identify alias people set set_once register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "), n = 0; n < o.length; n++) g(u, o[n]); e._i.push([i, s, a]); }), (e.__SV = 1));
  })(document, window.posthog || []);
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    session_recording: { recordCrossOriginIframes: true, capturePerformance: false },
  });
  /* eslint-enable */
}

function loadGtag() {
  if (!GA4_ID && !ADS_ID) return;
  if (window.gtag) {
    // gtag.js is already loaded by the owner-supplied Google Ads snippet in
    // index.html — only attach the (consent-gated) GA4 config, never re-load
    // the library or re-run the Ads config (would double-count pageviews).
    if (GA4_ID) window.gtag("config", GA4_ID, { anonymize_ip: true });
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID || ADS_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  if (GA4_ID) window.gtag("config", GA4_ID, { anonymize_ip: true });
  // Google Ads tag — same gtag.js library, loaded once for both IDs.
  if (ADS_ID) window.gtag("config", ADS_ID);
}

// Real-user Core Web Vitals → GA4 (+ PostHog). Loaded on demand after consent
// so the web-vitals bundle never sits in the critical path. No PII is sent —
// only metric id, value, rating and page path. In GA4, create an exploration
// or weekly scheduled report over events lcp/cls/inp/fcp/ttfb (metric_value,
// metric_rating) for ongoing field monitoring.
async function loadWebVitals() {
  try {
    const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import("web-vitals");
    const send = (name) => (metric) => {
      const props = {
        metric_id: metric.id,
        metric_value: Math.round(name === "CLS" ? metric.value * 1000 : metric.value),
        metric_rating: metric.rating,
        page: window.location.pathname,
      };
      if (window.gtag) window.gtag("event", name.toLowerCase(), props);
      if (window.posthog && window.posthog.capture) window.posthog.capture(`cwv_${name.toLowerCase()}`, props);
    };
    onLCP(send("LCP"));
    onCLS(send("CLS"));
    onINP(send("INP"));
    onFCP(send("FCP"));
    onTTFB(send("TTFB"));
  } catch { /* web-vitals unavailable — monitoring is best-effort */ }
}

export function loadAnalytics() {
  if (loaded || typeof window === "undefined" || IS_SNAPSHOT) return;
  if (getConsent() !== "granted") return;
  loaded = true;
  loadPostHog();
  loadGtag();
  loadWebVitals();
  // Flush events that fired before the stubs existed (e.g. mount-time view
  // events whose effects run before the consent banner's loadAnalytics).
  while (pending.length) {
    const [n, p] = pending.shift();
    emit(n, p);
  }
}

// Fire a conversion / interaction event across whatever is loaded.
// Canonical CRO event names are emitted alongside the mission-specified names
// (quote_submit_success, booking_confirmed, …) and legacy PostHog names so
// GA4 key events, the Ads setup and existing dashboards all keep working.
// In GA4, mark only ONE name per action as a key event to avoid doubles.
const EVENT_ALIASES = {
  quote_open: ["start_form"],
  quote_submit: ["submit_quote", "quote_submit_success"],
  quote_error: ["quote_submit_error"],
  whatsapp_click: ["click_whatsapp"],
  whatsapp_clicked: ["click_whatsapp"],
  phone_clicked: ["click_phone"],
  click_primary_cta: ["click_quote"],
  sample_report_view: ["view_sample_report"],
  booking_started: ["booking_open"],
  booking_completed: ["booking_confirmed"],
  availability_fallback_click: ["urgent_slot_request"],
};

const pending = [];

// Google Ads conversion actions (owner-provided send_to IDs from the Google
// Ads account). WhatsApp clicks are covered by the delegated listener in the
// index.html Ads snippet — only the quote-form conversion fires from here,
// from the form's success handler (never on the button click itself).
const ADS_CONVERSIONS = {
  quote_submit: "rgEZCIGYlP8cEObRucpE",
};

function emitAdsConversion(name, props) {
  if (!ADS_ID || typeof window.gtag !== "function" || !ADS_CONVERSIONS[name]) return;
  // Real lead value in AED when the caller provides one (e.g. the indicative
  // quote price), otherwise the neutral 1.0.
  const value = typeof props?.conversion_value === "number" ? props.conversion_value : 1.0;
  window.gtag("event", "conversion", {
    send_to: `${ADS_ID}/${ADS_CONVERSIONS[name]}`,
    value,
    currency: "AED",
  });
}

function emit(n, props) {
  if (window.posthog && window.posthog.capture) window.posthog.capture(n, props);
  if (window.gtag) window.gtag("event", n, props);
  emitAdsConversion(n, props);
}

export function trackEvent(name, props = {}) {
  if (typeof window === "undefined" || IS_SNAPSHOT) return;
  const names = [name, ...(EVENT_ALIASES[name] || [])];
  const ready = (window.posthog && window.posthog.capture) || window.gtag;
  if (!ready) {
    // Queue only when consent may still be granted; flush happens in
    // loadAnalytics(). Never queue after an explicit decline.
    if (getConsent() !== "declined" && pending.length < 100) {
      names.forEach((n) => pending.push([n, props]));
    }
    return;
  }
  for (const n of names) emit(n, props);
}
